// @flow
import * as React from 'react';
import { TextInput as GrommetInput, Stack } from 'grommet';
import { defineMessages } from 'react-intl';
import * as Yup from 'yup';
import { get } from 'lodash';
import FieldWrapper from './FieldWrapper';
import StyledCloseButton from './StyledCloseButton';


type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: {
        setFieldValue: (string, any) => void,
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
    },
    required: boolean,
    label: string,
    help_text: string,
    disabled?: boolean,
}

const messages = defineMessages({
    min: {
        id: 'emailinput-min',
        defaultMessage: 'The length of this field must be more than {minLimit} characters!',
    },
    max: {
        id: 'emailinput-max',
        defaultMessage: 'The length of this field must be less than {maxLimit} characters!',
    },
    valid: {
        id: 'emailinput-valid',
        defaultMessage: 'Enter a valid email.',
    },
});

// eslint-disable-next-line
const emailRegex = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

// eslint-disable-next-line
class EmailInput extends React.Component<Props> {
    static getValidationSchema(field: { validation: Object }) {
        let errMsg = Yup.string().matches(emailRegex, messages.valid).nullable();
        if (field.validation.required) {
            errMsg = Yup.string().matches(emailRegex, messages.valid).required(messages.required).nullable();
        }
        if (field.validation.min_length) {
            errMsg = errMsg.min(field.validation.min_length.toString(),
                { ...messages.min, values: { minLimit: field.validation.min_length.toString() } });
        }
        if (field.validation.max_length) {
            errMsg = errMsg.max(field.validation.max_length.toString(),
                { ...messages.max, values: { maxLimit: field.validation.max_length.toString() } });
        }

        return errMsg;
    }

    handleChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
        const { form: { setFieldValue, setFieldTouched }, field: { name } } = this.props;
        setFieldValue(name, e.target.value);
        setFieldTouched(name, true, true);
    };

    onClear = () => {
        const { form: { setFieldValue }, field: { name } } = this.props;
        setFieldValue(name, null);
    };

    render() {
        const {
            disabled,
            field: {
                name, onBlur, value,
            },
            form: { errors, touched, isSubmitting },
            label,
            help_text: help,
            required,
        } = this.props;

        return (
            <FieldWrapper
                name={name}
                required={required}
                helpText={help}
                label={label}
                placeholder=""
            >
                <Stack anchor="right">
                    <GrommetInput
                        name={name}
                        onChange={this.handleChange}
                        placeholder=""
                        onBlur={onBlur}
                        value={value || ''}
                        id={`id-${name}`}
                        errors={get(errors, name)}
                        touched={get(touched, name)}
                        disabled={disabled || isSubmitting}
                    />
                    {value && <StyledCloseButton onClick={this.onClear} plain />}
                </Stack>
            </FieldWrapper>
        );
    }
}

EmailInput.defaultProps = {
    disabled: false,
};

export default EmailInput;
